import { useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@proptr/api-client/react';
import { ApiError } from '@proptr/api-client';
import {
  AlertIcon,
  ArrowIcon,
  AuthSplitShell,
} from '../components/layout/AuthSplitShell.js';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Banner = { tone: 'error' | 'warn'; title?: string; message: string };

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const flash = (location.state as { flash?: string } | null)?.flash;

  const errorRef = useRef<HTMLDivElement>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [banner, setBanner] = useState<Banner | null>(null);
  const [loading, setLoading] = useState(false);

  const normalizedEmail = email.trim();
  const formValid = EMAIL_PATTERN.test(normalizedEmail) && password.length > 0;

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!formValid || loading) return;
    setBanner(null);
    setLoading(true);
    try {
      await login(normalizedEmail, password);
      navigate('/', { replace: true });
    } catch (err) {
      setBanner(bannerFor(err));
      requestAnimationFrame(() => errorRef.current?.focus());
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthSplitShell>
      <form className="auth-form" noValidate onSubmit={(e) => void onSubmit(e)}>
        <h1>Sign in</h1>
        <p className="auth-subtitle">
          Pick up where your brokerage left off.
        </p>

        {flash && (
          <div className="auth-note">
            <AlertIcon />
            <p>{flash}</p>
          </div>
        )}

        {banner && (
          <div
            ref={errorRef}
            className={banner.tone === 'warn' ? 'auth-banner is-warn' : 'auth-banner'}
            role="alert"
            tabIndex={-1}
          >
            <AlertIcon />
            <p>
              {banner.title && <strong>{banner.title} </strong>}
              {banner.message}
            </p>
          </div>
        )}

        <label htmlFor="email">Email address</label>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="username"
          placeholder="you@proptr.ee"
          value={email}
          disabled={loading}
          autoFocus
          onChange={(e) => {
            setEmail(e.target.value);
            setBanner(null);
          }}
        />

        <div className="auth-password-label">
          <label htmlFor="password">Password</label>
          <Link to="/forgot-password">Forgot?</Link>
        </div>
        <div style={{ position: 'relative' }}>
          <input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="••••••••"
            className="auth-password-input"
            value={password}
            disabled={loading}
            onChange={(e) => {
              setPassword(e.target.value);
              setBanner(null);
            }}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            tabIndex={-1}
            style={{
              position: 'absolute',
              right: 14,
              top: '50%',
              transform: 'translateY(-50%)',
              border: 0,
              background: 'none',
              cursor: 'pointer',
              color: '#9a9ab4',
              display: 'flex',
            }}
          >
            {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
          </button>
        </div>

        <button
          type="submit"
          className="auth-primary-button auth-login-button"
          disabled={!formValid || loading}
          aria-busy={loading}
        >
          {loading ? (
            <>
              <span className="auth-spinner" aria-hidden="true" /> Signing you in
            </>
          ) : (
            <>
              Log in <ArrowIcon />
            </>
          )}
        </button>
      </form>
    </AuthSplitShell>
  );
}

function bannerFor(err: unknown): Banner {
  if (err instanceof ApiError) {
    if (err.code === 'AUTH_NOT_MANAGER') {
      return {
        tone: 'warn',
        title: 'Manager access required.',
        message:
          'Your account exists but does not have Manager access. Contact your administrator if you believe this is a mistake.',
      };
    }
    if (err.code === 'AUTH_ACCOUNT_INACTIVE') {
      return {
        tone: 'warn',
        title: 'Account is not active.',
        message: 'Contact your administrator to reactivate it.',
      };
    }
    if (err.status === 401) {
      return {
        tone: 'error',
        title: 'That didn’t work.',
        message: 'That email and password don’t match. Please try again.',
      };
    }
    return { tone: 'error', message: err.message || 'Sign in failed.' };
  }
  return {
    tone: 'error',
    message:
      'We couldn’t reach the sign-in service. Nothing is wrong with your account — please try again.',
  };
}
